// src/algorithms/ordered/bayer.ts

/** Build a Bayer threshold matrix of size N (power of 2). Output normalized 0..1. */
export function bayerMatrix(n: number): Float32Array {
  if ((n & (n - 1)) !== 0 || n < 2) throw new Error('Bayer size must be power of 2 >= 2');
  // Recursive: M_{2n} = ( 4*M_n          4*M_n + 2
  //                       4*M_n + 3      4*M_n + 1 )
  let m: number[][] = [[0, 2], [3, 1]];
  let size = 2;
  while (size < n) {
    const next: number[][] = Array.from({ length: size * 2 }, () => Array(size * 2).fill(0));
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
      const v = m[y]![x]!;
      next[y]![x] = 4 * v;
      next[y]![x + size] = 4 * v + 2;
      next[y + size]![x] = 4 * v + 3;
      next[y + size]![x + size] = 4 * v + 1;
    }
    m = next;
    size *= 2;
  }
  const flat = new Float32Array(n * n);
  const denom = n * n;
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    flat[y * n + x] = (m[y]![x]! + 0.5) / denom;
  }
  return flat;
}

export function uploadBayerTexture(
  gl: WebGL2RenderingContext, n: number,
): { tex: WebGLTexture; size: number } {
  const data = bayerMatrix(n);
  const bytes = new Uint8Array(n * n);
  for (let i = 0; i < data.length; i++) bytes[i] = Math.round(data[i]! * 255);
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, n, n, 0, gl.RED, gl.UNSIGNED_BYTE, bytes);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  return { tex, size: n };
}
